import type { interfaces } from "inversify";
import { action, computed, observable } from "mobx";
import type { IItemsRepository, IListRepository } from "../repositories/types";
import type { ISelectItem } from "../types";

export interface IParams {
  [property: string]: any;
}

export default class OptionsViewModel<TModel = any> {
  @observable items: TModel[] = [];
  @observable private hasActualData = false;
  @observable private currentlyFetching = false;
  @observable private params: IParams = {};

  constructor(
    private repository: IListRepository<TModel> | IItemsRepository<TModel> | any,
    private formatFunction: (model: TModel) => ISelectItem,
    private loaderMethod: string = "getAll",
    private loaderParamNames: string[] = []
  ) {}

  @action.bound
  setLoaderParams(params?: IParams) {
    let needReload = false;
    for (const key in params) {
      if (this.loaderParamNames.includes(key) && this.params[key] !== params[key]) {
        this.params[key] = params[key];
        needReload = true;
      }
    }

    if (needReload) {
      this.invalidate();
      this.fetchItems();
    }
  }

  async fetchItems() {
    if (!this.currentlyFetching) {
      this.currentlyFetching = true;
      const data = await this.repository[this.loaderMethod || "getAll"]({ offset: 0, limit: 100, ...this.params });
      this.items = data.items;
      this.hasActualData = true;
      this.currentlyFetching = false;
    }
  }

  @computed
  get hasAllParamsSet() {
    return this.loaderParamNames.every(paramName => this.params[paramName] !== undefined);
  }

  @computed
  get options() {
    if (!this.hasActualData && this.hasAllParamsSet) {
      this.fetchItems();
    }

    return this.items.map(item => this.formatFunction(item));
  }

  @computed
  get isLoaded() {
    return this.hasActualData && !this.currentlyFetching;
  }

  invalidate() {
    this.hasActualData = false;
    this.items = [];
  }

  static Factory<TModel = any>({ container }: interfaces.Context) {
    return (
      repository: interfaces.ServiceIdentifier<IListRepository<TModel> | IItemsRepository<TModel>>,
      formatFunction: (model: TModel) => ISelectItem,
      loaderMethod?: string,
      loaderParams?: string[]
    ) => {
      return new OptionsViewModel<TModel>(
        container.get(repository),
        formatFunction,
        loaderMethod,
        loaderParams
      ) as OptionsViewModel<any>;
    };
  }
}
